import {GUID} from '../guids'
import {AlertColumnName, AlertLevel, AlertMessage, AlertViewSettings, HubChannel, LimitType, SortType, ToastsList, User,} from '../types/types'
import {
  GreenButtonVirtualServer,
  GreenButtonFailed,
  OrderNoticeAdminResponseReceived,
  RedButtonVirtualServer,
  RedButtonFailed,
  UpdateGreenButtonState,
  UpdateRedButtonState,
} from './redbuttons'
import {ClientCode, ClientConfig} from "../types/clients";

export interface ClockTick {
  readonly type: 'CLOCK_TICK'
  readonly newLocalTime: Date
}

export interface Init {
  readonly type: 'INIT'
}
export interface DisplayToasts {
  readonly toasts: ToastsList
  readonly type: 'DISPLAY_TOASTS'
}

export interface RemoveToast {
  readonly type: 'REMOVE_TOAST'
  readonly id: GUID
}

export interface UpdateAllClientTCSLimits {
  readonly type: 'UPDATE_ALL_CLIENT_TCS_LIMITS'
}

export interface SubToClientDataHubSucceeded {
  readonly hubConnection: signalR.HubConnection | 'SKIPPING'
  readonly type: 'SUBSCRIBE_TO_CLIENT_DATA_HUB_SUCCEEDED'
}

export interface SubToClientDataHubFailed {
  readonly type: 'SUBSCRIBE_TO_CLIENT_DATA_HUB_FAILED'
  readonly error: Error
}

export interface MessageFromClientDataHub {
  readonly type: 'MESSAGE_FROM_CLIENT_DATA_HUB'
  readonly channel: HubChannel
  readonly data: unknown
}

export interface SetUserLoggedInSuccessfully {
  readonly type: 'USER_LOGGED_IN'
  readonly user: User
}

export interface GetAutomaticRedButtonStatusSucceeded {
  readonly enabled: boolean
  readonly type: 'GET_AUTOMATIC_RED_BUTTON_STATUS_SUCCEEDED'
}

export interface SelectClient {
  readonly type: 'SELECT_CLIENT'
  readonly select: string | undefined
}

export interface SetClientSettings {
  readonly type: 'SET_CLIENT_SETTINGS'
  readonly clientSettings: ReadonlyArray<ClientConfig>
}

export interface SubmitLoginForm {
  readonly type: 'SUBMIT_LOGIN_FORM'
  readonly username: string
  readonly password: string
}

export interface SetLoginFailed {
  readonly type: 'LOGIN_FAILED'
}

export interface SetLogoutFailed {
  readonly type: 'LOGOUT_FAILED'
}

export interface SubmitLogout {
  readonly type: 'LOGOUT'
}

export interface SetUserNotLoggedIn {
  readonly type: 'NOT_LOGGED_IN'
}

export interface SetLoggingIn {
  readonly type: 'LOGGING_IN'
}

export interface ManuallyUpdateAlertLevel {
  readonly type: 'MANUALLY_UPDATE_ALERT_LEVEL'
  readonly newLevel: AlertLevel
}

export interface ToggleAlertsColumnMenu {
  readonly type: 'TOGGLE_ALERTS_COLUMN_MENU'
  readonly column: AlertColumnName
}

export interface SetAlertColumnSort {
  readonly type: 'SET_ALERT_COLUMN_SORT'
  readonly field: keyof AlertMessage
  readonly sortType: SortType
}

export interface SetAlertsClientFilter {
  readonly type: 'SET_ALERTS_CLIENT_FILTER'
  readonly filter: AlertViewSettings['filterClient']
}

export interface SetAlertsDirectionFilter {
  readonly type: 'SET_ALERTS_DIRECTION_FILTER'
  readonly filter: AlertViewSettings['filterDirection']
}

export interface SetAlertsSeverityFilter {
  readonly type: 'SET_ALERTS_SEVERITY_FILTER'
  readonly filter: AlertViewSettings['filterSeverityOrLevel']
}

export interface SetAlertsMetricFilter {
  readonly type: 'SET_ALERTS_METRIC_FILTER'
  readonly filter: AlertViewSettings['filterMetric']
}

export interface ClearAlertsFilters {
  readonly type: 'CLEAR_ALERTS_FILTERS'
}

export interface ToggleShowOnlyActiveAlerts {
  readonly type: 'TOGGLE_SHOW_ONLY_ACTIVE_ALERTS'
  readonly newValue: boolean | 'toggle'
}

export interface UpdateClientMetricMuted {
  readonly type: 'UPDATE_CLIENT_METRIC_MUTED'
  readonly clientCode: ClientCode
  readonly metric: LimitType
  readonly newState: 'muted' | 'not_muted' | 'toggle'
}

export type RootAction =
  | ClockTick
  | Init
  | SubToClientDataHubSucceeded
  | SubToClientDataHubFailed
  | MessageFromClientDataHub
  | SelectClient
  | SubmitLoginForm
  | SetLoggingIn
  | SetLoginFailed
  | SetUserLoggedInSuccessfully
  | SetUserNotLoggedIn
  | SubmitLogout
  | SetLogoutFailed
  | ManuallyUpdateAlertLevel
  | SetAlertsClientFilter
  | ToggleAlertsColumnMenu
  | SetAlertColumnSort
  | SetAlertsDirectionFilter
  | SetAlertsSeverityFilter
  | SetAlertsMetricFilter
  | ClearAlertsFilters
  | ToggleShowOnlyActiveAlerts
  | RedButtonVirtualServer
  | OrderNoticeAdminResponseReceived
  | RedButtonFailed
  | UpdateClientMetricMuted
  | UpdateGreenButtonState
  | UpdateRedButtonState
  | GreenButtonVirtualServer
  | GreenButtonFailed
  | DisplayToasts
  | RemoveToast
  | UpdateAllClientTCSLimits
  | GetAutomaticRedButtonStatusSucceeded
  | SetClientSettings

export function clockTick(timestamp: Date): ClockTick {
  return {
    newLocalTime: timestamp,
    type: 'CLOCK_TICK',
  }
}

export function init(): Init {
  return {
    type: 'INIT',
  }
}

export function submitLoginForm(
  username: string,
  password: string,
): SubmitLoginForm {
  return {
    password,
    type: 'SUBMIT_LOGIN_FORM',
    username,
  }
}

export function userLoggedInSuccessful(
  user: User,
): SetUserLoggedInSuccessfully {
  return {
    type: 'USER_LOGGED_IN',
    user,
  }
}

export function logout(): SubmitLogout {
  return {
    type: 'LOGOUT',
  }
}

export function loginFailed(): SetLoginFailed {
  return {
    type: 'LOGIN_FAILED',
  }
}

export function logoutFailed(error: Error): SetLogoutFailed {
  return {
    type: 'LOGOUT_FAILED',
  }
}

export function notLoggedIn(): SetUserNotLoggedIn {
  return {
    type: 'NOT_LOGGED_IN',
  }
}

export function subToClientDataHubSuccessful(
  hubConnection: signalR.HubConnection | 'SKIPPING',
): SubToClientDataHubSucceeded {
  return {
    hubConnection,
    type: 'SUBSCRIBE_TO_CLIENT_DATA_HUB_SUCCEEDED',
  }
}

export function subToClientDataHubFailed(
  error: Error,
): SubToClientDataHubFailed {
  return {
    error,
    type: 'SUBSCRIBE_TO_CLIENT_DATA_HUB_FAILED',
  }
}

export function messageFromClientDataHub(
  channel: HubChannel,
  data: unknown,
): MessageFromClientDataHub {
  return {
    channel,
    data,
    type: 'MESSAGE_FROM_CLIENT_DATA_HUB',
  }
}

export function selectClient(clientSymbol: string | undefined): SelectClient {
  // Should this throw if there's no such client to select?
  return {
    select: clientSymbol,
    type: 'SELECT_CLIENT',
  }
}

export function manuallyUpdateAlertLevel(
  newLevel: AlertLevel,
): ManuallyUpdateAlertLevel {
  return {
    newLevel,
    type: 'MANUALLY_UPDATE_ALERT_LEVEL',
  }
}

export function toggleAlertsColumnMenu(
  column: AlertColumnName,
): ToggleAlertsColumnMenu {
  return {
    column,
    type: 'TOGGLE_ALERTS_COLUMN_MENU',
  }
}

export function setAlertsColumnSort(
  field: keyof AlertMessage,
  sortType: SortType,
): SetAlertColumnSort {
  return {
    field,
    sortType,
    type: 'SET_ALERT_COLUMN_SORT',
  }
}

export function setAlertsClientFilter(
  filter: AlertViewSettings['filterClient'],
): SetAlertsClientFilter {
  return {
    filter,
    type: 'SET_ALERTS_CLIENT_FILTER',
  }
}

export function setAlertsDirectionFilter(
  filter: AlertViewSettings['filterDirection'],
): SetAlertsDirectionFilter {
  return {
    filter,
    type: 'SET_ALERTS_DIRECTION_FILTER',
  }
}

export function setAlertsSeverityFilter(
  filter: AlertViewSettings['filterSeverityOrLevel'],
): SetAlertsSeverityFilter {
  return {
    filter,
    type: 'SET_ALERTS_SEVERITY_FILTER',
  }
}

export function setAlertsMetricFilter(
  filter: AlertViewSettings['filterMetric'],
): SetAlertsMetricFilter {
  return {
    filter,
    type: 'SET_ALERTS_METRIC_FILTER',
  }
}

export function clearAlertsFilters(): ClearAlertsFilters {
  return {
    type: 'CLEAR_ALERTS_FILTERS',
  }
}

export function toggleShowOnlyActiveAlerts(
  newValue: ToggleShowOnlyActiveAlerts['newValue'],
): ToggleShowOnlyActiveAlerts {
  return {
    newValue,
    type: 'TOGGLE_SHOW_ONLY_ACTIVE_ALERTS',
  }
}

export function updateClientMetricMuted(
  clientCode: UpdateClientMetricMuted['clientCode'],
  metric: UpdateClientMetricMuted['metric'],
  newState: UpdateClientMetricMuted['newState'],
): UpdateClientMetricMuted {
  return {
    clientCode,
    metric,
    newState,
    type: 'UPDATE_CLIENT_METRIC_MUTED',
  }
}
export function displayToasts(toasts: ToastsList): DisplayToasts {
  return {
    toasts,
    type: 'DISPLAY_TOASTS',
  }
}

export function removeToast(id: GUID): RemoveToast {
  return {
    id,
    type: 'REMOVE_TOAST',
  }
}

export function updateAllClientTCSLimits(): UpdateAllClientTCSLimits {
  return {
    type: 'UPDATE_ALL_CLIENT_TCS_LIMITS',
  }
}

export function getAutomaticRedButtonStatusSucceeded(
  enabled: boolean,
): GetAutomaticRedButtonStatusSucceeded {
  return {
    enabled,
    type: 'GET_AUTOMATIC_RED_BUTTON_STATUS_SUCCEEDED',
  }
}


export const actionCreators = {
  selectClient: (clientCode: string | undefined) => <SelectClient>{ type: "SELECT_CLIENT", select: clientCode },
  setClientSettings: (clientSettings: ReadonlyArray<ClientConfig>) => <SetClientSettings>{type: "SET_CLIENT_SETTINGS", clientSettings: clientSettings}
}
